import { HttpClient } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_VERSION, ComplaintV1, ComplaintV1Attachment, ComplaintV1Message, ComplaintV1Status, complaintV1Statuses, ComplaintV1StatusesColors } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared/config.service';
import { core, StatsService, ToastService } from '@icc/shared/helpers/b2b';
import { DialogService } from '@icc/ui';
import { TranslateService } from '@ngx-translate/core';
import { B2bDocumentManagerService } from 'libs/b2b/sales-document/src/lib/b2b-document-manager.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject, Subscription } from 'rxjs';
import { ComplaintV1HistoryComponent } from '../complaint-v1-history/complaint-v1-history.component';

@Component({
    selector: 'icc-complaint-v1',
    templateUrl: './complaint-v1.component.html',
    styleUrls: ['./complaint-v1.component.scss'],
})
export class ComplaintV1Component {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    openedComplaintId?: string;
    complaint?: ComplaintV1;

    orderDocumentNumber: string[] = [];
    orderPositionTitle: string[] = [];

    hasAccessTo = {
        manage: false,
    };

    saving = false;
    preview = false;

    statusColorHex = 'ffffff';
    complaintStatuses = complaintV1Statuses;

    messages: ComplaintV1Message[] = [];
    newMessage = '';

    attachments: ComplaintV1Attachment[] = [];

    isComplaintForEntireOrder = true;

    private subscriptions: Subscription[] = [];

    constructor(
        private route: ActivatedRoute,
        private configService: ConfigService,
        private router: Router,
        private http: HttpClient,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        @Inject(APP_VERSION) private appVersion: string,
        private toastService: ToastService,
        private statsService: StatsService,
        private b2bDocumentManager: B2bDocumentManagerService,
        private dialogService: DialogService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                if (params.id) {
                    this.openedComplaintId = params.id;
                    this.loadRequest();
                } else {
                    this.isComplaintForEntireOrder = !params.salesDocumentPositionId;
                    this.createComplaint(params.salesDocumentId, params.salesDocumentPositionId ?? null);
                }
                this.statsService.add({
                    action: 'open-complaint',
                })
            }),
        );
        this.checkAccess();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    async checkAccess() {
        this.hasAccessTo.manage = await this.permissionsService.hasPermission('trade-complaint-v1-manage');
    }

    async loadRequest() {
        const {complaint} = await this.http.get<{
            complaint: ComplaintV1
        }>(`/api/complaint-v1/complaint/${this.openedComplaintId}`).toPromise();
        this.complaint = complaint;
        this.isComplaintForEntireOrder = this.complaint.salesDocumentPositionId === null;
        this.statusColorHex = ComplaintV1StatusesColors[this.complaint.status];
        this.orderDocumentNumber = this.complaint.orderDocumentNumber || [];
        if (!this.isComplaintForEntireOrder) {
            this.orderPositionTitle = this.complaint.orderPositionTitle || [];
        } else {
            const documentPositions = await this.http.get<any[]>(`/api/b2b/sales-document-positions/${this.complaint.salesDocumentId}`).toPromise();
            this.orderPositionTitle = documentPositions?.flatMap((pos) => pos.title || []) || [];
        }
        this.setDocumentCrumbs();
        this.loadMessages();
        this.loadAttachments();
    }

    async createComplaint(salesDocumentId: string, salesDocumentPositionId: string | null) {
        this.complaint = {
            uuid: core.generateUUID(),
            salesDocumentId,
            salesDocumentPositionId,
            documentNumber: [],
            sellerCompanyId: this.configService.user.user.contractorCompanyId,
            contractorId: +this.configService.user.user.ActiveCompany,
            contractorUserId: this.configService.user.user.id,
            contractorName: this.configService.user.user?.person?.reference_name,
            description: '',
            status: 'requested',
            created: new Date(),
            modified: new Date(),
        };
        this.statusColorHex = ComplaintV1StatusesColors[this.complaint.status];
        this.orderDocumentNumber = (await this.b2bDocumentManager.getDocument(salesDocumentId))?.documentNumber || [];
        const documentPositions = await this.b2bDocumentManager.getDocumentPosition(salesDocumentId);
        if (salesDocumentPositionId !== null) {
            this.orderPositionTitle = documentPositions?.find((pos) => pos.uuid === salesDocumentPositionId)?.title || [];
        } else {
            this.orderPositionTitle = documentPositions?.flatMap((pos) => pos.title || []) || [];
        }
        this.setDocumentCrumbs();
    }

    changeDescription(description: string) {
        this.complaint.description = description;
    }

    changeStatus(status: ComplaintV1Status) {
        this.complaint.status = status;
        this.statusColorHex = ComplaintV1StatusesColors[status];
        this.saveComplaint();
    }

    async sendComplaint() {
        const formData = new FormData();
        formData.append('complaint', JSON.stringify(this.complaint));
        formData.append('userId', String(this.configService?.user?.user?.id));
        formData.append('appVersion', this.appVersion);

        for (const attachment of this.attachments) {
            formData.append('attachment', attachment.data,  attachment.uuid);
        }
        formData.append('attachments', JSON.stringify(this.attachments));

        await this.http.post('/api/complaint-v1/complaint/create', formData).toPromise();
        this.router.navigate(['complaint-v1', 'complaints']);
    }

    async saveComplaint() {
        await this.http.post('/api/complaint-v1/complaint/' + this.complaint.uuid, {
            complaint: this.complaint,
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        this.toastService.add({
            title: this.translateService.instant('COMPLAINT|Zapisano zmiany'),
            body: this.translateService.instant('COMPLAINT|Zmiany zostały zapisane'),
        });
    }

    async loadMessages() {
        if (!(await this.permissionsService.hasPermission(['trade-complaint-v1-manage', 'trade-request-complaint-v1']))) {
            return;
        }
        const {messages} = await this.http.get<{
            messages: ComplaintV1Message[]
        }>(`/api/complaint-v1/complaint/${this.openedComplaintId}/messages`).toPromise();
        this.messages = messages;
    }

    async sendMessage() {
        await this.http.post('/api/complaint-v1/complaint/' + this.complaint.uuid + '/message', {
            message: this.newMessage,
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        this.newMessage = '';
        this.loadMessages();
    }

    setDocumentCrumbs(){
        ComplaintV1Component.crumbsSubject.next({
            level: 1,
            crumbs: [{
                label: this.translateService.instant('COMPLAINT|Lista zgłoszeń reklamacji'),
                url: '/complaint-v1/complaints',
            }]
        });

        if(this.openedComplaintId){
            ComplaintV1Component.crumbsSubject.next({
                level: 2,
                crumbs: [
                    {
                        label: this.translateService.instant('COMPLAINT|Zgłoszenie') + ' ' + (this.complaint?.documentNumber || []).join(''),
                        url: '/complaint-v1/complaint/'+this.openedComplaintId,
                    }
                ],
            });
        } else {
            ComplaintV1Component.crumbsSubject.next({
                level: 2,
                crumbs: [{
                    label: this.translateService.instant('COMPLAINT|Nowe zgłoszenie reklamacji'),
                    url: '/complaint-v1/complaint/new' + (this.complaint?.salesDocumentId ? `/${this.complaint.salesDocumentId}/${this.complaint.salesDocumentPositionId}` : ''),
                }],
            });
        }
    }

    async loadAttachments() {
        const {attachments} = await this.http.get<{
            attachments: ComplaintV1Attachment[]
        }>(`/api/complaint-v1/complaint/${this.openedComplaintId}/attachments`).toPromise();
        this.attachments = attachments;
    }

    async addAttachment(files: File[]) {
        for (const file of files) {
            if (!this.validateFile(file)) {
                continue;
            }
            this.attachments.push({
                uuid: core.generateUUID(),
                complaintId: +this.openedComplaintId,
                filename: file.name,
                type: file.type,
                data: file,
                created: new Date(),
            });
        }
    }

    validateFile(file: File): boolean {
        const result =
            file?.type &&
            ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', '.doc', '.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(
                file.type
            );

        if (!result) {
            const fileName = file?.name ? '( ' + file?.name + ' )' : '';
            this.toastService.add({
                title: this.translateService.instant('INTERFACE|Walidacja {fileName}', {
                    fileName,
                }),
                body: this.translateService.instant(
                    'INTERFACE|Akceptowane pliki to pdf, doc, docx, jpg i png.'
                ),
                severity: 'error',
            });
        }
        return result;
    }

    async deleteAttachment(attachment: ComplaintV1Attachment) {
        this.attachments = this.attachments.filter((a) => a.uuid !== attachment.uuid);
    }

    async sendHistory() {
        const result = await this.dialogService.open(ComplaintV1HistoryComponent, {
            data: {
                complaintNumber: this.complaint.documentNumber.join(''),
            },
        }).afterClosed().toPromise();
        if (result?.email) {
            await this.http.post('/api/complaint-v1/complaint/' + this.complaint.uuid + '/email-changelog', {
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
                email: result.email,
            }).toPromise();
        }
    }

    async requestTransport() {
        this.router.navigate(['transport', 'transport', 'complaint', this.complaint.uuid]);
    }

}
